import { Badge } from 'designSystem/components/Badge'
import { Button } from 'designSystem/components/Button'
import { Item, ItemGroup } from 'designSystem/components/Item'
import { ScrollArea } from 'designSystem/components/ScrollArea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'designSystem/components/Select'
import { Separator } from 'designSystem/components/Separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import {
  getComparableShortTermRateInspectionCdfGroups,
  getShortTermRateInspectionCdfGroups,
} from 'features/dataLayers/generation/shortTermCorrosionRate/inspectionChoices'
import { getShortTermCorrosionMatchConfidencePercent } from 'features/dataLayers/generation/shortTermCorrosionRate/priorMatching'
import type {
  ShortTermCorrosionFeatureRectangle,
  ShortTermCorrosionPriorSupport,
} from 'features/dataLayers/generation/shortTermCorrosionRate/types'
import type { ShortTermCorrosionRateGenerationParams } from 'features/dataLayers/generation/shortTermCorrosionRate/useGenerateShortTermCorrosionRateCDFs'
import {
  ChevronDown,
  Info,
  Loader2,
  RotateCw,
  Search,
  Trash2,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

import {
  EditableShiftMetric,
  FieldBlock,
  formatCount,
  PARAMETER_HELP_TEXT,
  ParameterInput,
  SHIFT_RESET_TOLERANCE_FEET,
  type WorkflowDisplayUnits,
} from './ParameterControls'

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

const SelectorsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.75rem;
`

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
`

const StepContainer = styled.div`
  display: flex;
  min-height: 0;
  flex: 1 1 0%;
  flex-direction: column;
  gap: 1.25rem;
`

const DetectSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const DetectButton = styled(Button)`
  height: 2.5rem;
  width: 100%;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
`

const SpinningLoader = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`

const AdvancedCard = styled.div`
  border: 1px solid var(--border);
  border-radius: calc(var(--radius) - 2px);
  background-color: var(--background);
`

const AdvancedToggle = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  text-align: left;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  transition:
    color 150ms,
    background-color 150ms,
    border-color 150ms;

  &:hover {
    background-color: color-mix(in oklab, var(--muted) 50%, transparent);
  }
`

const AdvancedChevron = styled(ChevronDown)<{ $open: boolean }>`
  width: 1rem;
  height: 1rem;
  color: var(--muted-foreground);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
`

const AdvancedBody = styled.div`
  border-top: 1px solid var(--border);
  padding: 0.75rem;
`

const SupportCard = styled.div`
  border: 1px solid var(--border);
  border-radius: calc(var(--radius) - 2px);
  background-color: var(--background);
  padding: 0.75rem;
  font-size: 0.75rem;
  line-height: 1rem;
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
`

const ResetButton = styled(Button)`
  margin-top: 0.75rem;
  width: 100%;
  justify-content: center;
`

const EmptyListPlaceholder = styled.div`
  min-height: 0;
  flex: 1 1 0%;
`

const ListScrollArea = styled(ScrollArea)`
  min-height: 0;
  flex: 1 1 0%;
`

const FeatureItemGroup = styled(ItemGroup)`
  gap: 0.5rem;
  padding-right: 0.25rem;
`

const FeatureItem = styled(Item)<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: 2rem 1fr auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  text-align: left;
  ${({ $selected }) =>
    $selected
      ? css`
          border-color: var(--primary);
          background-color: color-mix(
            in oklab,
            var(--primary) 10%,
            transparent
          );
        `
      : css`
          background-color: var(--background);

          &:hover {
            background-color: color-mix(
              in oklab,
              var(--muted) 50%,
              transparent
            );
          }
        `}
`

const IndexBadge = styled.span`
  display: flex;
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  background-color: var(--muted);
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 600;
`

const FeatureBadges = styled.span`
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--muted-foreground);
`

const DeleteAction = styled.span<{ $pending: boolean }>`
  display: flex;
  height: 2rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 500;
  transition:
    color 150ms,
    background-color 150ms;
  ${({ $pending }) =>
    $pending
      ? css`
          background-color: var(--destructive);
          color: var(--destructive-foreground);

          &:hover {
            background-color: color-mix(
              in oklab,
              var(--destructive) 90%,
              transparent
            );
          }
        `
      : css`
          width: 2rem;
          color: var(--muted-foreground);

          &:hover {
            background-color: color-mix(
              in oklab,
              var(--destructive) 10%,
              transparent
            );
            color: var(--destructive);
          }
        `}
`

const HelpTooltipContent = styled(TooltipContent)`
  max-width: 18rem;
  border: 1px solid var(--border);
  border-radius: calc(var(--radius) - 2px);
  background-color: var(--popover);
  padding: 0.75rem;
  color: var(--popover-foreground);
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
`

const WideTooltipContent = styled(HelpTooltipContent)`
  max-width: 20rem;
`

const TooltipText = styled.p`
  font-size: 0.75rem;
  line-height: 1.25rem;
  color: var(--muted-foreground);
`

const ConfidenceLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.625rem;
  text-transform: uppercase;
  color: var(--muted-foreground);
`

const ConfidenceInfoIcon = styled.span`
  display: inline-flex;
  width: 0.875rem;
  height: 0.875rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  color: var(--muted-foreground);
`

const ConfidenceValue = styled.div`
  margin-top: 0.125rem;
  font-weight: 500;
`

export enum WizardStep {
  Features = 'features',
  Matches = 'matches',
  Rate = 'rate',
}

export type AsyncStatus = 'idle' | 'running'

export function InputSelectors({
  targetGroupId,
  comparisonGroupId,
  targetGroups,
  comparisonGroups,
  onTargetChange,
  onComparisonChange,
}: {
  targetGroupId?: string
  comparisonGroupId?: string
  targetGroups: readonly ReturnType<
    typeof getShortTermRateInspectionCdfGroups
  >[number][]
  comparisonGroups: readonly ReturnType<
    typeof getComparableShortTermRateInspectionCdfGroups
  >[number][]
  onTargetChange: (value: string) => void
  onComparisonChange: (value: string) => void
}) {
  return (
    <SelectorsGrid>
      <TwoColumnGrid>
        <FieldBlock label='Target'>
          <Select value={targetGroupId} onValueChange={onTargetChange}>
            <SelectTrigger>
              <SelectValue placeholder='Target' />
            </SelectTrigger>
            <SelectContent align='start' position='popper' side='bottom'>
              {targetGroups.map((group) => (
                <SelectItem key={group.inspectionId} value={group.inspectionId}>
                  {group.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldBlock>

        <FieldBlock label='Prior'>
          <Select value={comparisonGroupId} onValueChange={onComparisonChange}>
            <SelectTrigger>
              <SelectValue placeholder='Prior' />
            </SelectTrigger>
            <SelectContent align='start' position='popper' side='bottom'>
              {comparisonGroups.map((group) => (
                <SelectItem key={group.inspectionId} value={group.inspectionId}>
                  {group.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldBlock>
      </TwoColumnGrid>
    </SelectorsGrid>
  )
}

export function FeatureStepPanel({
  featureRectangles,
  selectedFeatureId,
  hasDetected,
  observationCount,
  canDetect,
  asyncStatus,
  params,
  displayUnits,
  onDetect,
  onSelectFeature,
  onDeleteFeature,
  onParamChange,
}: {
  featureRectangles: readonly ShortTermCorrosionFeatureRectangle[]
  selectedFeatureId?: string
  hasDetected: boolean
  observationCount: number
  canDetect: boolean
  asyncStatus: AsyncStatus
  params: ShortTermCorrosionRateGenerationParams
  displayUnits: WorkflowDisplayUnits
  onDetect: () => void
  onSelectFeature: (featureId: string) => void
  onDeleteFeature: (featureId: string) => void
  onParamChange: (
    updates: Partial<ShortTermCorrosionRateGenerationParams>
  ) => void
}) {
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false)

  return (
    <StepContainer>
      <Separator />
      <DetectSection>
        <BadgeRow>
          <Badge variant='outline'>
            {formatCount(observationCount, 'observation')}
          </Badge>
          <Badge variant='outline'>
            {formatCount(featureRectangles.length, 'feature')}
          </Badge>
        </BadgeRow>
        <DetectButton size='lg' disabled={!canDetect} onClick={onDetect}>
          {asyncStatus === 'running' ? (
            <SpinningLoader size={16} />
          ) : (
            <Search size={16} />
          )}
          {hasDetected ? 'Redetect features' : 'Detect features'}
        </DetectButton>
      </DetectSection>

      <AdvancedCard>
        <AdvancedToggle
          type='button'
          aria-expanded={advancedSettingsOpen}
          onClick={() => setAdvancedSettingsOpen((open) => !open)}>
          <span>Advanced settings</span>
          <AdvancedChevron $open={advancedSettingsOpen} />
        </AdvancedToggle>
        {advancedSettingsOpen && (
          <AdvancedBody>
            <TwoColumnGrid>
              <ParameterInput
                label='Core DfN (%)'
                help={PARAMETER_HELP_TEXT.coreDfn}
                value={params.target_feature_core_dfn_percent}
                onChange={(value) =>
                  onParamChange({ target_feature_core_dfn_percent: value })
                }
              />
              <ParameterInput
                label='Grow DfN (%)'
                help={PARAMETER_HELP_TEXT.growDfn}
                value={params.target_feature_grow_dfn_percent}
                onChange={(value) =>
                  onParamChange({ target_feature_grow_dfn_percent: value })
                }
              />
              <ParameterInput
                label={`Min zone area (${displayUnits.areaLabel})`}
                help={PARAMETER_HELP_TEXT.minArea}
                value={displayUnits.areaFromFeetSquared(
                  params.min_feature_area_feet_squared
                )}
                step={displayUnits.areaStep}
                onChange={(value) =>
                  onParamChange({
                    min_feature_area_feet_squared:
                      displayUnits.feetSquaredFromArea(value),
                  })
                }
              />
              <ParameterInput
                label={`Merge (${displayUnits.lengthLabel})`}
                help={PARAMETER_HELP_TEXT.merge}
                value={displayUnits.lengthFromFeet(
                  params.target_feature_merge_distance_feet
                )}
                step={displayUnits.lengthStep}
                onChange={(value) =>
                  onParamChange({
                    target_feature_merge_distance_feet:
                      displayUnits.feetFromLength(value),
                  })
                }
              />
              <ParameterInput
                label={`Max zone area (${displayUnits.areaLabel})`}
                help={PARAMETER_HELP_TEXT.maxArea}
                value={displayUnits.areaFromFeetSquared(
                  params.target_feature_max_rect_area_feet_squared
                )}
                step={displayUnits.areaStep}
                onChange={(value) =>
                  onParamChange({
                    target_feature_max_rect_area_feet_squared:
                      displayUnits.feetSquaredFromArea(value),
                  })
                }
              />
              <ParameterInput
                label={`Search (${displayUnits.lengthLabel})`}
                help={PARAMETER_HELP_TEXT.search}
                value={displayUnits.lengthFromFeet(
                  params.prior_support_search_radius_feet
                )}
                step={displayUnits.lengthStep}
                onChange={(value) =>
                  onParamChange({
                    prior_support_search_radius_feet:
                      displayUnits.feetFromLength(value),
                  })
                }
              />
              <ParameterInput
                label='Match step (bins)'
                help={PARAMETER_HELP_TEXT.matchStep}
                value={params.prior_support_translation_step_bins}
                step={1}
                onChange={(value) =>
                  onParamChange({
                    prior_support_translation_step_bins: value,
                  })
                }
              />
              <ParameterInput
                label='Distance weight'
                help={PARAMETER_HELP_TEXT.distanceWeight}
                value={params.prior_support_distance_weight}
                step={0.01}
                onChange={(value) =>
                  onParamChange({
                    prior_support_distance_weight: value,
                  })
                }
              />
            </TwoColumnGrid>
          </AdvancedBody>
        )}
      </AdvancedCard>

      <FeatureRectangleList
        featureRectangles={featureRectangles}
        selectedFeatureId={selectedFeatureId}
        displayUnits={displayUnits}
        onSelectFeature={onSelectFeature}
        onDeleteFeature={onDeleteFeature}
      />
    </StepContainer>
  )
}

export function MatchesStepPanel({
  featureRectangles,
  priorSupports,
  algorithmPriorSupports,
  selectedFeatureId,
  displayUnits,
  onSelectFeature,
  onSupportShiftChange,
  onSupportShiftReset,
}: {
  featureRectangles: readonly ShortTermCorrosionFeatureRectangle[]
  priorSupports: readonly ShortTermCorrosionPriorSupport[]
  algorithmPriorSupports: readonly ShortTermCorrosionPriorSupport[]
  selectedFeatureId?: string
  displayUnits: WorkflowDisplayUnits
  onSelectFeature: (featureId: string) => void
  onSupportShiftChange: (props: {
    featureId: string
    shiftXFeet: number
    shiftYFeet: number
  }) => void
  onSupportShiftReset: (featureId: string) => void
}) {
  const selectedSupport = priorSupports.find(
    (support) => support.featureId === selectedFeatureId
  )
  const selectedAlgorithmSupport = algorithmPriorSupports.find(
    (support) => support.featureId === selectedFeatureId
  )
  const hasEditedShift =
    !!selectedSupport &&
    !!selectedAlgorithmSupport &&
    hasEditedSupportShift(selectedSupport, selectedAlgorithmSupport)

  return (
    <StepContainer>
      <Separator />
      {selectedSupport && (
        <SupportCard>
          <MetricsGrid>
            <ConfidenceMetric support={selectedSupport} />
            <EditableShiftMetric
              label='Shift X'
              valueFeet={selectedSupport.shiftXFeet}
              displayUnits={displayUnits}
              onChange={(shiftXFeet) =>
                onSupportShiftChange({
                  featureId: selectedSupport.featureId,
                  shiftXFeet,
                  shiftYFeet: selectedSupport.shiftYFeet,
                })
              }
            />
            <EditableShiftMetric
              label='Shift Y'
              valueFeet={selectedSupport.shiftYFeet}
              displayUnits={displayUnits}
              onChange={(shiftYFeet) =>
                onSupportShiftChange({
                  featureId: selectedSupport.featureId,
                  shiftXFeet: selectedSupport.shiftXFeet,
                  shiftYFeet,
                })
              }
            />
          </MetricsGrid>
          {hasEditedShift && (
            <ResetButton
              type='button'
              variant='outline'
              size='sm'
              onClick={() => onSupportShiftReset(selectedSupport.featureId)}>
              <RotateCw size={14} />
              Reset to algorithm match
            </ResetButton>
          )}
        </SupportCard>
      )}
      <FeatureRectangleList
        featureRectangles={featureRectangles}
        selectedFeatureId={selectedFeatureId}
        displayUnits={displayUnits}
        onSelectFeature={onSelectFeature}
      />
    </StepContainer>
  )
}

export function FeatureRectangleList({
  featureRectangles,
  selectedFeatureId,
  displayUnits,
  onSelectFeature,
  onDeleteFeature,
}: {
  featureRectangles: readonly ShortTermCorrosionFeatureRectangle[]
  selectedFeatureId?: string
  displayUnits: WorkflowDisplayUnits
  onSelectFeature: (featureId: string) => void
  onDeleteFeature?: (featureId: string) => void
}) {
  const [pendingDeleteFeatureId, setPendingDeleteFeatureId] = useState<
    string | null
  >(null)
  const confirmDeleteButtonRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!pendingDeleteFeatureId) return
    const handlePointerDown = (event: PointerEvent) => {
      const button = confirmDeleteButtonRef.current
      if (button?.contains(event.target as Node)) return
      setPendingDeleteFeatureId(null)
    }
    document.addEventListener('pointerdown', handlePointerDown, true)
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [pendingDeleteFeatureId])

  if (featureRectangles.length === 0) {
    return <EmptyListPlaceholder />
  }

  return (
    <ListScrollArea>
      <FeatureItemGroup>
        {featureRectangles.map((featureRectangle, index) => {
          const isDeletePending = pendingDeleteFeatureId === featureRectangle.id
          return (
            <FeatureItem
              key={featureRectangle.id}
              asChild
              variant='outline'
              $selected={selectedFeatureId === featureRectangle.id}>
              <button
                type='button'
                onClick={() => onSelectFeature(featureRectangle.id)}>
                <IndexBadge>{index + 1}</IndexBadge>
                <FeatureBadges>
                  <FeatureSourceOrSeverityBadge
                    featureRectangle={featureRectangle}
                  />
                  <Badge variant='outline'>
                    {displayUnits.formatArea(featureRectangle.areaFeetSquared)}
                  </Badge>
                </FeatureBadges>
                {onDeleteFeature && (
                  <DeleteAction
                    ref={isDeletePending ? confirmDeleteButtonRef : undefined}
                    role='button'
                    tabIndex={0}
                    aria-label={
                      isDeletePending
                        ? `Confirm delete ${featureRectangle.label ?? featureRectangle.id}`
                        : `Delete ${featureRectangle.label ?? featureRectangle.id}`
                    }
                    $pending={isDeletePending}
                    onClick={(event) => {
                      event.stopPropagation()
                      if (isDeletePending) {
                        onDeleteFeature(featureRectangle.id)
                        setPendingDeleteFeatureId(null)
                        return
                      }
                      setPendingDeleteFeatureId(featureRectangle.id)
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      event.stopPropagation()
                      if (isDeletePending) {
                        onDeleteFeature(featureRectangle.id)
                        setPendingDeleteFeatureId(null)
                        return
                      }
                      setPendingDeleteFeatureId(featureRectangle.id)
                    }}>
                    {isDeletePending ? 'Delete' : <Trash2 size={16} />}
                  </DeleteAction>
                )}
              </button>
            </FeatureItem>
          )
        })}
      </FeatureItemGroup>
    </ListScrollArea>
  )
}

export function FeatureSourceOrSeverityBadge({
  featureRectangle,
}: {
  featureRectangle: ShortTermCorrosionFeatureRectangle
}) {
  if (Number.isFinite(featureRectangle.severeDfnPercent)) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant='outline'>
            Max Wastage{' '}
            {formatWastagePercent(featureRectangle.severeDfnPercent)}
          </Badge>
        </TooltipTrigger>
        <HelpTooltipContent side='top' align='start' sideOffset={8}>
          <TooltipText>
            Estimated from mean DfN minus 2 standard deviations within this
            zone, displayed as positive wastage.
          </TooltipText>
        </HelpTooltipContent>
      </Tooltip>
    )
  }

  return (
    <Badge variant='outline'>{formatFeatureSource(featureRectangle)}</Badge>
  )
}

export function ConfidenceMetric({
  support,
}: {
  support: ShortTermCorrosionPriorSupport
}) {
  return (
    <div>
      <ConfidenceLabel>
        Confidence
        <Tooltip>
          <TooltipTrigger asChild>
            <ConfidenceInfoIcon>
              <Info size={12} />
            </ConfidenceInfoIcon>
          </TooltipTrigger>
          <WideTooltipContent side='top' align='start' sideOffset={8}>
            <TooltipText>
              Confidence = 100 * (1 - penalty), where penalty = 0.60 * pattern
              mismatch + 0.25 * normalized distance + 0.15 * (1 - usable
              overlap). The result is clamped from 0% to 100%.
            </TooltipText>
          </WideTooltipContent>
        </Tooltip>
      </ConfidenceLabel>
      <ConfidenceValue>
        {getShortTermCorrosionMatchConfidencePercent(support)}%
      </ConfidenceValue>
    </div>
  )
}

function formatWastagePercent(value: number | undefined): string {
  return Number.isFinite(value) ? `${Math.round(Math.abs(value ?? 0))}%` : '-'
}

export function formatFeatureSource(
  featureRectangle: ShortTermCorrosionFeatureRectangle
): string {
  return featureRectangle.source === 'observation' ? 'Observation' : 'Detected'
}

export function hasEditedSupportShift(
  support: ShortTermCorrosionPriorSupport,
  algorithmSupport: ShortTermCorrosionPriorSupport
): boolean {
  return (
    Math.abs(support.shiftXFeet - algorithmSupport.shiftXFeet) >
      SHIFT_RESET_TOLERANCE_FEET ||
    Math.abs(support.shiftYFeet - algorithmSupport.shiftYFeet) >
      SHIFT_RESET_TOLERANCE_FEET
  )
}
